import { useBilling } from 'src/billing/context/provider';

// ----------------------------------------------------------------------

/** Shown on the locked Integrations entry in the Organization Settings sidebar. */
export const INTEGRATIONS_LOCKED_CAPTION = 'Available on the Professional plan';

export type IntegrationsAccess = {
  /** The org may use Integrations: a dev account, or a plan that includes them (Professional). */
  allowed: boolean;
  /** The plan answer has not arrived yet. */
  isLoading: boolean;
  /** The plan could not be read, so neither "allowed" nor "locked" can be claimed. */
  hasError: boolean;
  /** Settled, readable, and not allowed: show the lock. */
  isLocked: boolean;
  retry: () => Promise<unknown>;
};

/**
 * Whether the org may use the Integrations page. Reads the SERVER's answer only
 * (`planLimits.hasIntegrations` from /api/billing/org-status), never the optimistic pending plan
 * `useSubscription` keeps in localStorage, so the page unlocks exactly when the integrations host starts
 * accepting its calls. Fails closed: `allowed` stays false until the answer is in. Keyed off org-status
 * alone - the invoices call that also feeds `isBillingLoading` has nothing to say about this.
 */
export function useIntegrationsAccess(): IntegrationsAccess {
  const { orgStatus, planLimits, isDevAccount, billingError, revalidateOrgStatus } = useBilling();

  const hasStatus = orgStatus != null;
  const hasError = !hasStatus && billingError != null;
  const isLoading = !hasStatus && !hasError;
  const allowed = hasStatus && (isDevAccount || planLimits?.hasIntegrations === true);

  return {
    allowed,
    isLoading,
    hasError,
    isLocked: hasStatus && !allowed,
    retry: revalidateOrgStatus,
  };
}
